import type { ExtensoOptions } from './types'
import normalize from './utils/normalize'
import parse from './utils/parse'
import translate from './utils/translate'
import DecimalSeparators from './ts/enum/decimal-separators.enum'
import Modes from './ts/enum/modes.enum'
import validateOptions from './utils/validate-options'
import removeAccents from './utils/remove-accents'
import convert from './core/convert'
import resolveConversion from './core/resolve-conversion'

export type {
    BuiltInCurrencyOptions,
    CurrencyCode,
    CurrencyDefinition,
    CurrencyOptions,
    DecimalSeparator,
    ExtensoGender,
    ExtensoLocale,
    ExtensoMode,
    ExtensoOptions,
    ExtensoScale,
    NumberOptions,
} from './types'

const NEGATIVE_SIGN = '-'

/**
 * Converts a number to its written form in Portuguese
 * @param input - The number to convert (can be string, number, or bigint)
 * @param options - Configuration options for the conversion
 * @param options.mode - The conversion mode (ABBREVIATED, CURRENCY, DIGIT, or NUMBER)
 * @param options.decimalSeparator - The decimal separator to use (POINT or COMMA)
 * @param options.locale - The locale to use for the output (BR or PT)
 * @param options.scale - The number scale to use (SHORT or LONG)
 * @param options.removeAccents - Whether to remove accents from the output
 * @param options.currency - Currency configuration when mode is CURRENCY
 * @param options.number - Number configuration when mode is NUMBER
 * @param options.number.ordinal - Whether to write an integer as an ordinal
 * @returns The written form of the number in Portuguese
 * @throws {TypeError} If input is not a string, number, or bigint
 * @throws {Error} If the number format is invalid
 * @throws {Error} If the number exceeds scale limits
 * @throws {Error} If an invalid currency code is provided
 * @example
 * extenso(1234.56) // "mil duzentos e trinta e quatro vírgula cinquenta e seis"
 * extenso(1234.56, { mode: Modes.CURRENCY, currency: { code: Currencies.BRL } }) // "mil duzentos e trinta e quatro reais e cinquenta e seis centavos"
 * extenso("R$ 1234.56", { mode: Modes.CURRENCY }) // "mil duzentos e trinta e quatro reais e cinquenta e seis centavos"
 */
const extenso = (input: number | string | bigint, options: ExtensoOptions = {}): string => {
    validateOptions(options)

    const inputIsNumber = typeof input === 'number'

    const { currency, mode } = resolveConversion(input, options)

    input = normalize(input)
    const parseSeparator = inputIsNumber ? DecimalSeparators.POINT : options?.decimalSeparator
    const decimalSeparator = parseSeparator === DecimalSeparators.COMMA ? ',' : '.'
    const hasDecimal = input.includes(decimalSeparator)
    const { integer, decimal } = parse(input, parseSeparator, mode === Modes.DIGIT)
    const isNonZero = /[1-9]/.test(integer) || /[1-9]/.test(decimal)
    let text = convert({
        integer,
        decimal,
        decimalSeparator,
        hasDecimalSeparator: hasDecimal,
    }, mode, currency, options)

    text = translate(text, options.locale)

    if (input.startsWith(NEGATIVE_SIGN) && isNonZero) {
        text = `menos ${text}`
    }

    if (options.removeAccents) {
        text = removeAccents(text)
    }

    return text
}

export default extenso
